import { useId } from 'react'
import type { TrainingScreen } from './types'
import './training-screen.css'

export interface PermitTrainingScreenProps {
  screen: TrainingScreen
  selectedTargetId?: string
  showHint?: boolean
  onSelectTarget: (id: string) => void
}

/**
 * A labelled, simulated permit panel. Selecting a region reports an intention to
 * the learning page only; this component never signs or changes a real record.
 * The parent owns the explanation, step progression and learning credit.
 */
export function PermitTrainingScreen({
  screen,
  selectedTargetId,
  showHint = false,
  onSelectTarget,
}: PermitTrainingScreenProps) {
  const componentId = `ptwt-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const titleId = `${componentId}-title`
  const instructionId = `${componentId}-instruction`

  return (
    <section
      className="ptw-training"
      data-testid="training-screen"
      aria-labelledby={titleId}
      aria-describedby={instructionId}
    >
      <div className="ptwt-training-banner">
        <span className="ptwt-training-symbol" aria-hidden="true">i</span>
        <strong>Training copy — no real permit actions</strong>
      </div>

      <header className="ptwt-permit-header">
        <div className="ptwt-permit-identity">
          <span className="ptwt-caption">Permit to Work</span>
          <span className="ptwt-permit-number">{screen.permitNumber}</span>
        </div>
        <div className="ptwt-record-status">
          <span className="ptwt-caption">Recorded status</span>
          <strong>{screen.status}</strong>
        </div>
      </header>

      <div className="ptwt-section-context" aria-label="Current permit section">
        <span className="ptwt-caption">Current section</span>
        <strong className="ptwt-section-name">{screen.tab}</strong>
      </div>

      <div className="ptwt-screen-body">
        <h2 id={titleId} className="ptwt-screen-title">{screen.title}</h2>
        <p id={instructionId} className="ptwt-annotation">{screen.annotation}</p>

        <div className="ptwt-regions">
          {screen.regions.map((region) => {
            const selected = selectedTargetId === region.id
            const hinted = showHint && region.id === screen.correctTargetId
            const correct = region.id === screen.correctTargetId
            const feedbackId = `${componentId}-${region.id}-feedback`
            const cue = region.kind === 'action' || region.kind === 'choice' ? 'Choose' : 'Inspect'
            return (
              <div key={region.id} className="ptwt-region-wrap">
                <button
                  type="button"
                  className={[
                    'ptwt-region',
                    `ptwt-region-${region.kind}`,
                    selected ? 'is-selected' : '',
                    hinted ? 'is-hinted' : '',
                  ].filter(Boolean).join(' ')}
                  data-testid={`screen-region-${region.id}`}
                  aria-pressed={selected}
                  aria-describedby={`${instructionId}${selected ? ` ${feedbackId}` : ''}`}
                  aria-label={`${region.label}: ${region.value}${selected ? ', selected' : ''}${hinted ? ', hint target' : ''}`}
                  onClick={() => onSelectTarget(region.id)}
                >
                  <span className="ptwt-region-copy">
                    <span className="ptwt-region-label">{region.label}</span>
                    <span className="ptwt-region-value">{region.value}</span>
                  </span>
                  <span className="ptwt-region-trailing">
                    <span className="ptwt-target-cue" data-testid="training-target-cue" aria-hidden="true">
                      {cue}
                      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
                        <path d="M4 10h11M11 5l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    {(selected || hinted) && (
                      <span className="ptwt-region-markers">
                        {selected && <span className="ptwt-selected-marker">Inspected</span>}
                        {hinted && <span className="ptwt-hint-marker" data-testid="hint-target">Look here</span>}
                      </span>
                    )}
                  </span>
                </button>
                {selected && <div
                  id={feedbackId}
                  className={`ptwt-feedback ${correct ? 'is-correct' : 'is-retry'}`}
                  data-testid="step-feedback"
                  data-correct={correct}
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <span className="ptwt-feedback-symbol" aria-hidden="true">{correct ? '✓' : '!'}</span>
                  <p><strong>{correct ? 'Good choice.' : 'Take another look.'}</strong> {region.feedback}</p>
                </div>}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default PermitTrainingScreen
